import { useMemo } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useCollectionBinding } from '@vibesites/runtime/react';
import { useStaggerReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

interface Floorplan {
  floorplan_id?: string | null;
  floorplan_name?: string | null;
  floorplan_image_url?: string | null;
  floorplan_beds_min?: string | null;
  floorplan_beds_max?: string | null;
  floorplan_baths_min?: string | null;
  floorplan_sqft_min?: string | null;
  floorplan_sqft_max?: string | null;
  floorplan_price_min?: string | null;
  floorplan_price_max?: string | null;
  floorplan_available_units?: string | null;
}

interface BedroomCategory {
  key: string;
  glyph: string;
  label: string;
  param: string;
}

const BED_ORDER = ['studio', 'one', 'two', 'three', 'four', 'five'];

function bedCountToKey(bedsRaw: string | null | undefined): string {
  if (!bedsRaw) return 'studio';
  const trimmed = bedsRaw.trim().toLowerCase();
  if (trimmed === '0' || trimmed === 'studio' || trimmed === 'std') return 'studio';
  const num = parseInt(trimmed, 10);
  if (isNaN(num) || num <= 0) return 'studio';
  const names = ['studio', 'one', 'two', 'three', 'four', 'five'];
  return names[num] || `${num}`;
}

function keyToGlyph(key: string): string {
  if (key === 'studio') return 'S';
  const idx = BED_ORDER.indexOf(key);
  if (idx > 0) return String(idx);
  const num = parseInt(key, 10);
  return !isNaN(num) && num > 0 ? String(num) : key;
}

function keyToCaption(key: string): string {
  return key === 'studio' ? 'Studio' : 'Bedroom';
}

function groupFloorplansByBeds(floorplans: Floorplan[]): BedroomCategory[] {
  const groups: Record<string, Floorplan[]> = {};

  for (const plan of floorplans) {
    const key = bedCountToKey(plan.floorplan_beds_min);
    if (!groups[key]) groups[key] = [];
    groups[key].push(plan);
  }

  const sorted = Object.keys(groups).sort((a, b) => {
    const ai = BED_ORDER.indexOf(a);
    const bi = BED_ORDER.indexOf(b);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });

  return sorted.map((key) => ({
    key,
    glyph: keyToGlyph(key),
    label: keyToCaption(key),
    param: key,
  }));
}

export function FloorplansList() {
  const floorplans = useCollectionBinding<Floorplan>('floorplanslist.items', {
    collection: 'floorplans',
  });

  const categories = useMemo(
    () => (floorplans?.length ? groupFloorplansByBeds(floorplans) : []),
    [floorplans],
  );

  useRevealStyles();
  const rowRef = useStaggerReveal<HTMLDivElement>({ staggerMs: 100 });

  const hasCategories = categories.length > 0;
  const sparse = categories.length > 0 && categories.length <= 2;

  return (
    <section className="fp-bedrooms bg-white py-2xl md:py-3xl">
      <div
        className={`mx-auto px-md sm:px-lg ${sparse ? 'max-w-3xl' : 'max-w-[1400px]'}`}
      >
        {hasCategories ? (
          <div
            ref={rowRef}
            className={`fp-bedrooms-row flex flex-col items-center justify-center gap-2xl md:flex-row md:flex-wrap md:items-end ${
              sparse ? 'md:gap-4xl' : 'md:gap-0 md:justify-evenly'
            }`}
            data-count={categories.length}
          >
            {categories.map((cat) => (
              <a
                key={cat.key}
                data-reveal
                href={`/floor-plans?bedroom=${cat.param}`}
                className="reveal-fade-up fp-bedroom-item group flex flex-col items-center text-center no-underline"
                aria-label={`${cat.key === 'studio' ? 'Studio' : `${cat.glyph} Bedroom`} floor plans`}
              >
                <span className="fp-bedroom-glyph transition-transform duration-normal group-hover:scale-105">
                  {cat.glyph}
                </span>
                <span className="fp-bedroom-label mt-md font-body text-sm md:text-base tracking-[0.18em]">
                  {cat.label}
                </span>
              </a>
            ))}
          </div>
        ) : (
          <div className="text-center py-xl text-muted-foreground">
            <p className="font-body text-lg">Floor plans will be displayed here.</p>
          </div>
        )}
      </div>

      <style>{`
        .fp-bedroom-glyph {
          font-family: Georgia, 'Times New Roman', 'Palatino Linotype', serif;
          font-size: clamp(4.5rem, 9vw, 7.5rem);
          font-weight: 400;
          line-height: 0.9;
          letter-spacing: 0.02em;
          color: var(--vibe-color-primary);
          background: linear-gradient(
            165deg,
            color-mix(in srgb, var(--vibe-color-primary) 78%, white) 0%,
            var(--vibe-color-primary) 45%,
            color-mix(in srgb, var(--vibe-color-primary) 72%, black) 100%
          );
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        .fp-bedroom-label {
          color: var(--vibe-color-primary);
        }
        .fp-bedroom-item:hover .fp-bedroom-label {
          opacity: 0.8;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FloorplansList',
  displayName: 'Floor Plans List',
  tokens: {},
  slotGroups: {},
  collectionBindings: {
    'floorplanslist.items': {
      label: 'Floor Plans',
      collection: 'floorplans',
    },
  },
};

(FloorplansList as any).contentSchema = contentSchema;

export default FloorplansList;
